import type { FaqEntry, OfficeLocation, TeamMember, Testimonial } from '../types/content';

/**
 * Every record below is an unverified placeholder. Real people, real offices and
 * real testimonials must replace these, with evidence recorded, before this site
 * is published. The administrator Pre-launch Review screen lists them all.
 */

export const teamMembers: TeamMember[] = [
  {
    id: 'team-1',
    name: '[Managing Partner name]',
    title: 'Managing Partner',
    practice: 'Board and Chief Executive Practice',
    biography:
      'Leads the firm’s board and chief executive practice and holds overall responsibility for search quality and candidate care. Replace this biography with the appointee’s verified career history before publication.',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'team-2',
    name: '[Partner name]',
    title: 'Partner, Technology and Data',
    practice: 'Technology, Data and Artificial Intelligence',
    biography:
      'Responsible for technology, data and security leadership mandates across the client base. Replace with verified biography and credentials before publication.',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'team-3',
    name: '[Partner name]',
    title: 'Partner, Healthcare',
    practice: 'Healthcare and Health Technology',
    biography:
      'Leads provider, payer and health-technology mandates, including clinical leadership appointments. Replace with verified biography before publication.',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'team-4',
    name: '[Head of Research name]',
    title: 'Head of Research',
    practice: 'Research and Market Intelligence',
    biography:
      'Owns the firm’s research methodology, market mapping standards and talent-intelligence output. Replace with verified biography before publication.',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'team-5',
    name: '[Head of Candidate Care name]',
    title: 'Head of Candidate Care and Privacy',
    practice: 'Candidate Experience',
    biography:
      'Accountable for consent handling, candidate communication standards and privacy request processing. Replace with verified biography before publication.',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'team-6',
    name: '[Partner name]',
    title: 'Partner, Private Equity',
    practice: 'Private Equity and Portfolio Operations',
    biography:
      'Leads fund-level and portfolio-company leadership mandates across the deal cycle. Replace with verified biography before publication.',
    verificationState: 'unverified_placeholder',
  },
];

export const officeLocations: OfficeLocation[] = [
  {
    id: 'office-1',
    label: '[Primary office city]',
    addressLines: ['[Street address]', '[City, State ZIP]', 'United States'],
    coverage: 'United States — East',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'office-2',
    label: '[Second office city]',
    addressLines: ['[Street address]', '[City, State ZIP]', 'United States'],
    coverage: 'United States — Central and West',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'office-3',
    label: '[International office city]',
    addressLines: ['[Street address]', '[City, Postcode]', '[Country]'],
    coverage: 'Europe, Middle East and Africa',
    verificationState: 'unverified_placeholder',
  },
];

export const coverageRegions = [
  {
    id: 'coverage-us',
    label: 'United States',
    detail:
      'Search delivered nationally across all listed industry practices. [Confirm the states in which the firm is registered to operate and any professional licensing requirements before publication.]',
    isPlaceholder: true,
  },
  {
    id: 'coverage-emea',
    label: 'Europe, Middle East and Africa',
    detail:
      '[Confirm which EMEA markets the firm covers directly and which are covered through partner arrangements. Do not describe a partner network unless one exists and is documented.]',
    isPlaceholder: true,
  },
  {
    id: 'coverage-apac',
    label: 'Asia-Pacific',
    detail:
      '[Confirm Asia-Pacific coverage model before publication. State plainly whether coverage is direct or through arrangement.]',
    isPlaceholder: true,
  },
  {
    id: 'coverage-americas',
    label: 'Americas, excluding the United States',
    detail:
      '[Confirm coverage for Canada and Latin America, including any data-transfer implications for candidate information, before publication.]',
    isPlaceholder: true,
  },
];

export const testimonials: Testimonial[] = [
  {
    id: 'testimonial-1',
    attribution: '[Client name and organization]',
    role: '[Client role — e.g. Board Chair]',
    quote:
      '[Replace with a verified client statement. Record the date given, the individual’s written permission to publish, and the mandate it refers to. Do not publish a testimonial without all three.]',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'testimonial-2',
    attribution: '[Candidate name]',
    role: '[Appointed role and organization]',
    quote:
      '[Replace with a verified appointed-candidate statement. Written permission to publish and the appointment date must be recorded before publication.]',
    verificationState: 'unverified_placeholder',
  },
  {
    id: 'testimonial-3',
    attribution: '[Client name and organization]',
    role: '[Client role]',
    quote:
      '[Replace with a verified client statement. Evidence of permission must be attached to this record in the administrator dashboard.]',
    verificationState: 'unverified_placeholder',
  },
];

export const values = [
  {
    title: 'Discretion as a working method',
    detail:
      'Confidentiality is an operational discipline, not a promise in a brochure. Access to a mandate is limited to the assigned search team, candidate interest is never disclosed without authorization, and every disclosure decision is recorded.',
  },
  {
    title: 'Evidence over impression',
    detail:
      'Every candidate we present is assessed against a written framework by two consultants. Where we do not have evidence, we say so rather than filling the gap with inference.',
  },
  {
    title: 'Candour with candidates',
    detail:
      'We tell candidates the status of a role, the reason for any restriction, and the outcome of their candidacy. We do not describe a proposed opportunity as a vacancy, and we do not leave people without an answer.',
  },
  {
    title: 'Selectivity',
    detail:
      'We accept mandates where we believe we can deliver a credible shortlist. Declining work we cannot do well protects both the client and the candidates we would otherwise approach.',
  },
  {
    title: 'Breadth by design',
    detail:
      'Narrow research produces narrow slates. We widen the addressable population deliberately and test every requirement in a specification for whether it is genuinely necessary.',
  },
  {
    title: 'Human accountability',
    detail:
      'Matching technology ranks; consultants decide. No adverse decision about a candidacy is made by automated means alone, and every such decision carries a named reviewer and a recorded reason.',
  },
];

export const searchPhilosophy = [
  {
    heading: 'The specification is the deliverable that matters most',
    body: 'More searches fail on a poorly calibrated specification than on poor research. We spend disproportionate time establishing what the role actually is, what the organization is willing to trade, and what would constitute success in the first eighteen months.',
  },
  {
    heading: 'Research defines the boundary, not just the output',
    body: 'A market map should show the client the edge of the search — who was considered and excluded, and why — rather than only the handful who progressed. That is what allows a board to have confidence that the shortlist is representative.',
  },
  {
    heading: 'Assessment is calibrated, not comparative',
    body: 'Candidates are assessed against the agreed framework rather than against each other. This produces a shortlist that can be explained, and feedback that is usable by the individuals who were not appointed.',
  },
  {
    heading: 'The candidate is a party to the process, not its subject',
    body: 'Candidates control what is shared and when. They are told the status of the role, the identity of the client before presentation or the reason it is restricted, and the outcome of their candidacy with reasoning.',
  },
];

export const confidentialityCommitments = [
  'A candidate’s identity, documents and expressed interest are never shared with any employer without that candidate’s recorded, mandate-specific authorization.',
  'Where a client’s identity is withheld, candidates are told that a restriction exists and why. Confidentiality is never used to obscure whether a vacancy is confirmed.',
  'Candidate documents are held in private storage and shared only through authenticated portal access. They are never sent as ordinary email attachments.',
  'Access to a mandate record is limited to the assigned search team and is logged.',
  'Internal assessment notes are held separately from the feedback shared with a candidate, and both are visible to the candidate on request.',
  'Every consent, authorization, decline and disclosure decision is recorded with a timestamp and forms part of an append-only record.',
];

export const ethicalStandards = [
  'We do not charge candidates for consideration against any mandate, and we never make consideration contingent on purchasing a service.',
  'We do not present a proposed or exploratory opportunity as a confirmed vacancy.',
  'We do not promise employment, interviews, compensation outcomes or offers to any candidate.',
  'We do not request account passwords, government identity numbers, dates of birth or banking details, and no form on this platform collects them.',
  'We do not use demographic information in assessment. Where it is voluntarily disclosed, it is held separately from the assessment record.',
  'We give every assessed candidate the option of written feedback, whatever the outcome.',
  'We decline mandates where the specification would require us to discriminate, or where the organization is unwilling to be honest with candidates about the conditions of the role.',
];

export const privacyPrinciples = [
  {
    title: 'Collect only what the decision requires',
    detail:
      'Registration asks for what is needed to assess candidacy. Executive documents are optional at registration and requested later only where genuinely relevant to a live mandate.',
  },
  {
    title: 'Consent is granular and revocable',
    detail:
      'Candidacy, named-employer presentation, confidential mandate consideration and marketing are separate consents. Each can be granted or withdrawn independently at any time.',
  },
  {
    title: 'Authorization is per mandate',
    detail:
      'Permission to seek authorization is not permission to present. Each presentation requires its own recorded authorization identifying the employer, or stating why disclosure is temporarily restricted.',
  },
  {
    title: 'Your record is visible to you',
    detail:
      'Consent history, application events, document review feedback and privacy requests are all visible in your portal. You can request a full data download at any time.',
  },
];

export const inclusiveSearchApproach = [
  'Research boundaries are widened deliberately across sector adjacency, organizational scale and non-linear career paths, because narrow research is the primary cause of narrow slates.',
  'Every requirement in a specification is tested for whether it is genuinely necessary or merely conventional, and unnecessary requirements are removed in writing with the client.',
  'All candidates for a mandate receive the same structured interview, assessed by two consultants against one written framework.',
  'Slate composition is reported to the client in aggregate at each stage, never with individual attribution.',
  'Demographic information is never required from a candidate and is never used in assessment.',
  'We discuss the conditions an appointee will actually join, and what would need to change for the appointment to succeed. An inclusive search into an unchanged environment is not a service.',
];

export const faqs: FaqEntry[] = [
  {
    id: 'faq-c1',
    audience: 'candidate',
    question: 'Will my current employer find out that I have registered?',
    answer:
      'No. Your profile is visible only to the assigned search team. Your identity is never shared with any employer without your recorded, mandate-specific authorization, and you may choose not to disclose your current employer on your profile at all.',
  },
  {
    id: 'faq-c2',
    audience: 'candidate',
    question: 'Do I have to upload every executive document to register?',
    answer:
      'No. Only your name, professional email, location, current title, seniority and privacy consent are required. A résumé is helpful but optional at registration. The search team will request specific documents when they are genuinely relevant to a live mandate.',
  },
  {
    id: 'faq-c3',
    audience: 'candidate',
    question: 'What does a match indicator actually mean?',
    answer:
      'It is a ranking signal produced by comparing your profile against a mandate’s requirements across ten dimensions. It is not a determination that you are qualified, and it is not a guarantee of consideration. A consultant reviews every match before anything happens.',
  },
  {
    id: 'faq-c4',
    audience: 'candidate',
    question: 'Can I be rejected automatically by the system?',
    answer:
      'No. No adverse decision is made by automated means alone. Before an application can be recorded as not selected, a named consultant must record a reason. You may request that reasoning.',
  },
  {
    id: 'faq-c5',
    audience: 'candidate',
    question: 'Do I have to buy a career service to be considered?',
    answer:
      'No, and it would be improper to suggest otherwise. Executive Career Services are optional, candidate-paid and commercially separate from search. Declining them has no effect on your candidacy or your access to any opportunity.',
  },
  {
    id: 'faq-e1',
    audience: 'employer',
    question: 'Why does my account need verification before I can see candidates?',
    answer:
      'Because candidate information is only ever released to a verified representative of the commissioning organization. Verification establishes that you are authorized to act for the organization named on the account.',
  },
  {
    id: 'faq-e2',
    audience: 'employer',
    question: 'Can we see the full candidate pool for our mandate?',
    answer:
      'No. You see only those candidates who have specifically authorized presentation for your mandate. Candidates who have not authorized presentation are not represented in your view in any form, including in counts.',
  },
  {
    id: 'faq-e3',
    audience: 'employer',
    question: 'Can we conduct a search without our name entering the market?',
    answer:
      'Yes. A confidential mandate withholds your identity from the market under a written disclosure protocol. Candidates are told that a restriction exists and why, and your identity is released to a candidate before their profile is presented to you.',
  },
  {
    id: 'faq-s1',
    audience: 'career_services',
    question: 'Does purchasing a service improve my chances?',
    answer:
      'No. Career services are delivered by a separate practice and have no bearing on how any application is assessed. Any suggestion otherwise would be a misrepresentation.',
  },
  {
    id: 'faq-s2',
    audience: 'career_services',
    question: 'Will you access my LinkedIn account to make changes?',
    answer:
      'No. We supply rewritten copy as a document for you to apply yourself. We never request or accept account credentials for any third-party service.',
  },
  {
    id: 'faq-p1',
    audience: 'privacy',
    question: 'How do I get a copy of everything you hold about me?',
    answer:
      'Submit a data download request from your portal privacy settings, or through the privacy request form on the contact page. You will receive a reference number and written confirmation of the outcome.',
  },
  {
    id: 'faq-p2',
    audience: 'privacy',
    question: 'What happens if I withdraw consent?',
    answer:
      'Withdrawal takes effect immediately for future processing and you will not be considered against further mandates. Records of past consents are retained because they evidence what was authorized at the time.',
  },
];
